import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import get from 'lodash.get';

// components
import { PdpSharedBlurbPcs } from '../PdpSharedBlurbs/PdpSharedBlurbPcs';
import { PdpSharedBlurbLeadTime } from '../PdpSharedBlurbs/PdpSharedBlurbLeadTime';
import { PdpSharedBlurbShipping } from '../PdpSharedBlurbs/PdpSharedBlurbShipping';
import { PdpSharedBlurbReturns } from '../PdpSharedBlurbs/PdpSharedBlurbReturns';
import { PdpSharedBlurbUrgency } from '../PdpSharedBlurbs/PdpSharedBlurbUrgency';
import { PdpSharedBlurbSupportNumber } from '../PdpSharedBlurbs/PdpSharedBlurbSupportNumber';
import { PdpSharedConsultAnExpertLazy } from '../PdpSharedConsultAnExpert/PdpSharedConsultAnExpertLazy';
import PdpSharedBlurbTradeRequestAppointment from '../PdpSharedBlurbTradeRequestAppointment/PdpSharedBlurbTradeRequestAppointment';

// helpers
import { shouldShowConsultAnExpert } from '../helpers/consultAnExpert';

// styles
import styles from './PdpRespSidebarBlurbs.scss';

export function PdpRespSidebarBlurbsComponent(props) {
    const {
        currency,
        relayVariables,
        viewer,
        item,
        user,
        displayInfo,
        vipUserRelationship,
        shipmentQuote,
        shipment,
        showLeadTimeBlurb,
        relay,
        isPdpFreeShippingOnlyTestVariant,
    } = props;
    const showConsultAnExpert =
        relayVariables.isClient &&
        !relayVariables.isTrade &&
        shouldShowConsultAnExpert({ viewer, item, user, isVip: relayVariables.isVip });
    const pricingDisplayInfo = get(displayInfo, 'pricing') || null;
    const renderBlurbs =
        !!pricingDisplayInfo && !pricingDisplayInfo.showSold && !pricingDisplayInfo.showUnavailable;
    if (!renderBlurbs) {
        return null;
    }

    // isPdpFreeShippingOnlyTestVariant NOTE: containerStyle applies the border top style
    // which is good b/c we always have at least shipping blurb in the div
    // but for this free shipping only variant test we're replacing the shipping blurb with pill next to pricing
    // so sometimes this div (with border top) would render but has nothing inside.
    // we have to not apply styles.container and let parent add the margin to sibling instead
    const containerStyle = !isPdpFreeShippingOnlyTestVariant ? styles.container : undefined;

    return (
        <div className={containerStyle}>
            {showConsultAnExpert && (
                <PdpSharedConsultAnExpertLazy
                    environment={relay.environment}
                    itemId={relayVariables.itemId}
                    userId={relayVariables.userId}
                />
            )}
            {!relayVariables.isTrade && <PdpSharedBlurbUrgency item={item} />}
            {!showConsultAnExpert && (
                <PdpSharedBlurbPcs
                    user={user}
                    vipUserRelationship={vipUserRelationship}
                    item={item}
                />
            )}
            {showLeadTimeBlurb && <PdpSharedBlurbLeadTime item={item} />}
            {!isPdpFreeShippingOnlyTestVariant && (
                <PdpSharedBlurbShipping
                    currency={currency}
                    itemId={relayVariables.itemId}
                    item={item}
                    userId={relayVariables.userId}
                    viewer={viewer}
                    user={user}
                    shipmentQuote={shipmentQuote}
                    shipment={shipment}
                    renderTooltip
                />
            )}
            {relayVariables.isTrade && (
                <PdpSharedBlurbTradeRequestAppointment
                    user={user}
                    viewer={viewer}
                    item={item}
                    showBadge
                    showCoachmark
                />
            )}
            <PdpSharedBlurbReturns
                item={item}
                itemId={relayVariables.itemId}
                user={user}
                userId={relayVariables.userId}
            />
            {!showConsultAnExpert && (
                <PdpSharedBlurbSupportNumber viewer={viewer} user={user} item={item} />
            )}
        </div>
    );
}

PdpRespSidebarBlurbsComponent.defaultProps = {
    showLeadTimeBlurb: true,
};

PdpRespSidebarBlurbsComponent.propTypes = {
    currency: PropTypes.string,
    relayVariables: PropTypes.shape({
        isTrade: PropTypes.bool,
        isVip: PropTypes.bool,
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isClient: PropTypes.bool,
    }).isRequired,
    viewer: PropTypes.object,
    item: PropTypes.object.isRequired,
    user: PropTypes.object,
    displayInfo: PropTypes.object,
    vipUserRelationship: PropTypes.object,
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
    showLeadTimeBlurb: PropTypes.bool,
    relay: PropTypes.object.isRequired,
    isPdpFreeShippingOnlyTestVariant: PropTypes.bool,
};

export const PdpRespSidebarBlurbs = createFragmentContainer(PdpRespSidebarBlurbsComponent, {
    viewer: graphql`
        fragment PdpRespSidebarBlurbs_viewer on Viewer {
            ...PdpSharedBlurbShipping_viewer
            ...PdpSharedBlurbSupportNumber_viewer
            ...consultAnExpert_viewer @skip(if: $isTrade)
            ...PdpSharedBlurbTradeRequestAppointment_viewer @include(if: $isTrade)
        }
    `,
    item: graphql`
        fragment PdpRespSidebarBlurbs_item on Item {
            ...PdpSharedBlurbLeadTime_item
            ...PdpSharedBlurbReturns_item
            ...PdpSharedBlurbShipping_item
            ...PdpSharedBlurbUrgency_item
            ...PdpSharedBlurbSupportNumber_item
            ...PdpSharedBlurbPcs_item
            ...consultAnExpert_item @skip(if: $isTrade)
            ...PdpSharedBlurbTradeRequestAppointment_item @include(if: $isTrade)
        }
    `,
    user: graphql`
        fragment PdpRespSidebarBlurbs_user on User {
            ...PdpSharedBlurbPcs_user
            ...PdpSharedBlurbShipping_user
            ...PdpSharedBlurbReturns_user
            ...PdpSharedBlurbShipping_user
            ...PdpSharedBlurbSupportNumber_user
            ...consultAnExpert_user @skip(if: $isTrade) @include(if: $isVip)
            ...PdpSharedBlurbTradeRequestAppointment_user @include(if: $isTrade)
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarBlurbs_displayInfo on ItemDisplayInfo {
            pricing {
                showSold
                showUnavailable
            }
        }
    `,
    vipUserRelationship: graphql`
        fragment PdpRespSidebarBlurbs_vipUserRelationship on UserRelationship {
            ...PdpSharedBlurbPcs_vipUserRelationship
        }
    `,
    shipmentQuote: graphql`
        fragment PdpRespSidebarBlurbs_shipmentQuote on ShipmentQuote {
            ...PdpSharedBlurbShipping_shipmentQuote
        }
    `,
    shipment: graphql`
        fragment PdpRespSidebarBlurbs_shipment on Shipment {
            ...PdpSharedBlurbShipping_shipment
        }
    `,
});
